/* Names in the reader's script.
 *
 * Everything the app writes itself is in the dictionary; what it shows from the records
 * - a driver's name, a car's model, the area a trip starts from, a city - arrived in
 * English letters, and a Telugu reader met "Ravi Kumar", "Swift Dzire" and "Benz
 * Circle" in the middle of a Telugu page. Here each is written in the reader's script,
 * for display only: no record changes, nothing leaves the phone, and English readers
 * see exactly what they saw.
 *
 * A word is looked up first (utils/localNameWords: cities, people's names, car words,
 * place words, colours) and otherwise spelled by sound. Telugu is the pivot: the word is
 * written in Telugu, then letter by letter in Kannada, Hindi or Tamil - the four scripts
 * keep their letters at the same places in Unicode, so one table serves all of them,
 * with each script's own habits applied (Hindi drops the final virama and writes "ś"
 * where Telugu names write "ṣ"; Tamil has no aspirates, voiced letters or nasal dot).
 */
import { useCallback } from "react";
import { useLanguage } from "../context/LanguageContext";
import { canonicalCity } from "./cities";
import { CITY_NATIVE, NAME_WORDS, CAR_WORDS, PLACE_WORDS, COLOR_WORDS, LETTER_NAMES } from "./localNameWords";

const COL = { te: 0, hi: 1, ta: 2, kn: 3 };
const TE = 0x0c00;
const ch = (off) => String.fromCharCode(TE + off);
const ZWNJ = String.fromCharCode(0x200c);
const VIRAMA = 0x4d, ANUSVARA = 0x02;

// ── English letters to Telugu, by sound ───────────────────────────────────────
// Vowels: [independent letter, sign after a consonant] - the sign of "a" is none.
const VOWEL = {
  a: [0x05, null], aa: [0x06, 0x3e], i: [0x07, 0x3f], ii: [0x08, 0x40], u: [0x09, 0x41], uu: [0x0a, 0x42],
  e: [0x0e, 0x46], ee: [0x0f, 0x47], ai: [0x10, 0x48], o: [0x12, 0x4a], oo: [0x13, 0x4b], au: [0x14, 0x4c],
};
const VOWS = [["aa", "aa"], ["ai", "ai"], ["au", "au"], ["ee", "ii"], ["ea", "ii"], ["ie", "ii"], ["ei", "ee"],
  ["oo", "uu"], ["ou", "au"], ["oa", "oo"], ["a", "a"], ["e", "E"], ["i", "i"], ["o", "O"], ["u", "u"]];
// Consonants, longest spelling first. Letters decided by context: SH, TH, C, D, T.
const CONS = [
  ["ksh", [0x15, 0x37]], ["chh", [0x1b]], ["sh", "SH"], ["ch", [0x1a]], ["th", "TH"], ["dh", [0x27]], ["kh", [0x16]],
  ["gh", [0x18]], ["ph", [0x2b]], ["bh", [0x2d]], ["jh", [0x1d]], ["ck", [0x15]],
  ["b", [0x2c]], ["c", "C"], ["d", "D"], ["f", [0x2b]], ["g", [0x17]], ["h", [0x39]], ["j", [0x1c]], ["k", [0x15]],
  ["l", [0x32]], ["m", [0x2e]], ["n", [0x28]], ["p", [0x2a]], ["q", [0x15]], ["r", [0x30]], ["s", [0x38]], ["t", "T"],
  ["v", [0x35]], ["w", [0x35]], ["x", [0x15, 0x38]], ["z", [0x1c]],
];
// Before these, n and m are the nasal dot (Chandu, Vamsi, Venkata); before others, a letter.
const STOPS = new Set([0x15, 0x16, 0x17, 0x18, 0x1a, 0x1b, 0x1c, 0x1d, 0x1f, 0x20, 0x21, 0x22, 0x24, 0x25, 0x26, 0x27,
  0x2a, 0x2b, 0x2c, 0x2d, 0x36, 0x37, 0x38]);

function soundUnits(word) {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  const out = [];
  for (let i = 0; i < w.length;) {
    const rest = w.slice(i);
    if (rest[0] === "y") {
      // A consonant before a vowel; after a consonant the vowel i ("Reddy"); after a
      // vowel it closes the syllable ("Vijay").
      const before = out[out.length - 1];
      if ("aeiou".includes(rest[1] || "_") || !before?.c) out.push({ c: [0x2f], src: "y" });
      else out.push({ v: "i", src: "y" });
      i += 1;
      continue;
    }
    const v = VOWS.find(([g]) => rest.startsWith(g));
    if (v) { out.push({ v: v[1], src: v[0] }); i += v[0].length; continue; }
    const c = CONS.find(([g]) => rest.startsWith(g));
    if (c) { out.push({ c: c[1], src: c[0] }); i += c[0].length; continue; }
    i += 1;
  }
  return out;
}

/* mode "name": Indian names, where t and d are dental (Satish, Dinesh) and "th" is a
   plain t (Anitha). mode "place": the same, but d is the retroflex letter most place
   names here carry (Vijayawada, Gudivada, -padu). mode "word": English words (car
   models), where t and d are the retroflex letters Indian scripts use for English (Tata,
   Diesel) and "th" is aspirated. */
function spellTelugu(word, mode) {
  const us = soundUnits(word);
  if (!us.length) return "";
  // A final e after a single consonant, with a vowel before it, is silent: Nellore, Ongole.
  const last = us.length - 1;
  if (us.length > 3 && us[last].v === "E" && us[last - 1]?.c && us[last - 2]?.v) us.pop();
  us.forEach((u, k) => {
    const prev = us[k - 1], next = us[k + 1];
    if (typeof u.c === "string") {
      if (u.c === "SH") u.c = [k === 0 && mode !== "word" ? 0x36 : 0x37];
      else if (u.c === "TH") u.c = [mode !== "word" ? 0x24 : 0x25];
      else if (u.c === "C") u.c = [next?.v && /^(i|ii|E|ee|e)$/.test(next.v) ? 0x38 : 0x15];
      else if (u.c === "D") u.c = [mode !== "name" || next?.src === "d" || prev?.src === "d" ? 0x21 : 0x26];
      else if (u.c === "T") u.c = [mode === "word" || next?.src === "t" || prev?.src === "t" ? 0x1f : 0x24];
    }
    // e and o are short before two consonants (Reddy, Gollapudi), long otherwise (Ramesh).
    if (u.v === "E" || u.v === "O") {
      const short = !!(next?.c && us[k + 2]?.c);
      u.v = u.v === "E" ? (short ? "e" : "ee") : (short ? "o" : "oo");
    }
  });
  if (us[0].src === "s" && us[1]?.src === "r") us[0].c = [0x36]; // Sri, Srinivas, Sravani
  let out = "";
  us.forEach((u, k) => {
    const prev = us[k - 1], next = us[k + 1];
    if (u.v) {
      const [ind, sign] = VOWEL[u.v];
      if (prev?.c) { if (sign) out += ch(sign); } else out += ch(ind);
      return;
    }
    if ((u.src === "n" || u.src === "m") && prev?.v && next?.c && STOPS.has(next.c[0])) { out += ch(ANUSVARA); return; }
    out += u.c.map(ch).join(ch(VIRAMA));
    if (!next || next.c) out += ch(VIRAMA);
  });
  return out;
}

// ── Telugu to the other scripts ─────────────────────────────────────────────────
const isTe = (c) => c >= 0x0c00 && c <= 0x0c7f;
const off = (s, i) => (isTe(s.charCodeAt(i)) ? s.charCodeAt(i) - TE : -1);
const isCons = (o) => o >= 0x15 && o <= 0x39;

/* ṣ in a Telugu name is ś to a Hindi or Kannada reader (Ramesh, Prakash), except where
   it is truly ṣ: after k (Lakshmi) and before ṭ or ṇ (Krishna). */
function shIsSha(s, i) {
  if (off(s, i) !== 0x37) return false;
  if (off(s, i - 1) === VIRAMA && off(s, i - 2) === 0x15) return false;
  if (off(s, i + 1) === VIRAMA && [0x1f, 0x20, 0x23].includes(off(s, i + 2))) return false;
  return true;
}

function toKannada(word) {
  let out = "";
  for (let i = 0; i < word.length; i += 1) {
    const c = word.charCodeAt(i);
    if (!isTe(c)) { out += word[i]; continue; }
    let o = c - TE;
    if (shIsSha(word, i)) o = 0x36;
    if (o === 0x58) o = 0x1a; else if (o === 0x59) o = 0x1c; else if (o === 0x5a) o = 0x31;
    out += String.fromCharCode(0x0c80 + o);
  }
  return out;
}

function toHindi(word, longA) {
  let out = "";
  for (let i = 0; i < word.length; i += 1) {
    const c = word.charCodeAt(i);
    if (!isTe(c)) { out += word[i]; continue; }
    let o = c - TE;
    if (o === 0x46) o = 0x47; else if (o === 0x4a) o = 0x4b; else if (o === 0x0e) o = 0x0f; else if (o === 0x12) o = 0x13;
    else if (o === 0x31) o = 0x30; else if (o === 0x33) o = 0x32;
    else if (shIsSha(word, i)) o = 0x36;
    out += String.fromCharCode(0x0900 + o);
  }
  // Hindi writes no virama at the end of a word (रमेश, not रमेश्), and a final nasal
  // dot as म (गन्नवरम).
  if (out.charCodeAt(out.length - 1) === 0x0900 + VIRAMA) out = out.slice(0, -1);
  else if (out.charCodeAt(out.length - 1) === 0x0900 + ANUSVARA) out = out.slice(0, -1) + String.fromCharCode(0x0900 + 0x2e);
  else if (longA && isCons(out.charCodeAt(out.length - 1) - 0x0900)) out += String.fromCharCode(0x0900 + 0x3e);
  return out;
}

const TAMIL_CONS = {
  0x15: 0x15, 0x16: 0x15, 0x17: 0x15, 0x18: 0x15, 0x19: 0x19, 0x1a: 0x1a, 0x1b: 0x1a, 0x1c: 0x1c, 0x1d: 0x1c,
  0x1e: 0x1e, 0x1f: 0x1f, 0x20: 0x1f, 0x21: 0x1f, 0x22: 0x1f, 0x23: 0x23, 0x24: 0x24, 0x25: 0x24, 0x26: 0x24,
  0x27: 0x24, 0x2a: 0x2a, 0x2b: 0x2a, 0x2c: 0x2a, 0x2d: 0x2a, 0x2e: 0x2e, 0x2f: 0x2f, 0x30: 0x30, 0x31: 0x31,
  0x32: 0x32, 0x33: 0x33, 0x35: 0x35, 0x36: 0x37, 0x37: 0x37, 0x39: 0x39,
};
const TA = (o) => String.fromCharCode(0x0b80 + o);
function tamilNasal(next) {
  if (next >= 0x15 && next <= 0x19) return 0x19;
  if (next >= 0x1a && next <= 0x1e) return 0x1e;
  if (next >= 0x1f && next <= 0x23) return 0x23;
  if (next >= 0x24 && next <= 0x28) return 0x28;
  return 0x2e;
}
/* Tamil: aspirated and voiced letters are written with their plain ones; the nasal dot
   becomes the nasal of the letter that follows; n is ந at the start and before த,
   ன elsewhere; s is ச between vowels and ஸ beside a consonant; f in an English word is
   ஃப. */
function toTamil(word, longA, english) {
  let out = "";
  for (let i = 0; i < word.length; i += 1) {
    const c = word.charCodeAt(i);
    if (!isTe(c)) { out += word[i]; continue; }
    const o = c - TE;
    const prev = off(word, i - 1), next = off(word, i + 1);
    if (o === ANUSVARA) { out += TA(tamilNasal(next)) + TA(VIRAMA); continue; }
    if (o === 0x43) { out += TA(VIRAMA) + TA(0x30) + TA(0x41); continue; } // vocalic r: ரு
    if (o === 0x28) {
      const initial = i === 0;
      const beforeT = next === VIRAMA && [0x24, 0x25, 0x26, 0x27].includes(off(word, i + 2));
      out += TA(initial || beforeT ? 0x28 : 0x29);
      continue;
    }
    if (o === 0x38) { out += TA(next === VIRAMA || prev === VIRAMA ? 0x38 : 0x1a); continue; }
    if (o === 0x2b && english) { out += String.fromCharCode(0x0b83) + TA(0x2a); continue; }
    if (isCons(o)) { out += TA(TAMIL_CONS[o] ?? o); continue; }
    out += TA(o);
  }
  const lastO = out.charCodeAt(out.length - 1) - 0x0b80;
  if (longA && isCons(lastO)) out += TA(0x3e);
  return out;
}

/* A word written in Telugu, in the reader's script. longA: the English ended in "a" and
   the reader's language writes it long (Hindi places, Tamil everything). */
function fromTelugu(word, lang, { longA = false, english = false } = {}) {
  if (lang === "te") return word;
  if (lang === "kn") return toKannada(word);
  if (lang === "hi") return toHindi(word, longA);
  if (lang === "ta") return toTamil(word, longA, english);
  return word;
}

// ── One word, by kind ─────────────────────────────────────────────────────────
const lookup = (dict, key, lang) => {
  const e = dict[key];
  if (!e) return null;
  if (typeof e === "string") return { te: e };
  return e;
};

// Letters read out one by one - VXi, AMT, the K after a name. Joined without a
// conjunct forming between them (ZWNJ), or with dots for initials.
function letters(token, lang, sep) {
  // Hindi reads the letter names long: पी, not पि.
  const I = String.fromCharCode(0x0900 + 0x3f), II = String.fromCharCode(0x0900 + 0x40);
  return [...token.toLowerCase()].filter((l) => LETTER_NAMES[l])
    .map((l) => fromTelugu(LETTER_NAMES[l], lang, { english: true }))
    .map((n) => (lang === "hi" && n.endsWith(I) ? n.slice(0, -1) + II : n)).join(sep);
}

const PLACE_SUFFIXES = [["wada", "వాడ"], ["vada", "వాడ"], ["puram", "పురం"], ["palem", "పాలెం"], ["palle", "పల్లె"], ["palli", "పల్లి"], ["pally", "పల్లి"],
  ["peta", "పేట"], ["pet", "పేట"], ["padu", "పాడు"], ["gudem", "గూడెం"], ["guda", "గూడ"], ["varam", "వరం"],
  ["nagar", "నగర్"], ["konda", "కొండ"], ["patnam", "పట్నం"], ["lanka", "లంక"]];

/* dotFollows: the record already puts a dot after this word ("K. Srinivasa Rao").
   mixed: the text has small letters, so a word in capitals is a code (MG Road). */
function wordIn(tokenRaw, lang, kind, { dotFollows = false, mixed = false } = {}) {
  const token = tokenRaw.toLowerCase();
  const endsA = /a$/.test(token);
  const tamilA = lang === "ta" && endsA;
  if (kind === "color" && COLOR_WORDS[token]) return COLOR_WORDS[token][COL[lang]];
  if (kind === "place" || kind === "city") {
    const city = canonicalCity(tokenRaw);
    if (city && CITY_NATIVE[city]) return CITY_NATIVE[city][COL[lang]];
  }
  const dicts = kind === "car" ? [CAR_WORDS, NAME_WORDS] : kind === "place" || kind === "city" ? [PLACE_WORDS, NAME_WORDS] : [NAME_WORDS];
  for (const d of dicts) {
    const e = lookup(d, token, lang);
    if (e) return e[lang] || fromTelugu(e.te, lang, { longA: tamilA || (lang === "hi" && endsA && kind !== "person"), english: kind === "car" });
  }
  if (kind === "car" || kind === "color") {
    // A code, not a word: no vowel, one letter, short capitals (AMT, GX), or capitals
    // ending in a small i (VXi, CRDi).
    if (!/[aeiou]/.test(token) || token.length === 1 || (/^[A-Z]+$/.test(tokenRaw) && token.length <= 4) || /^[A-Z]+i$/.test(tokenRaw)) {
      return letters(tokenRaw, lang, ZWNJ);
    }
  }
  if (kind === "person" && (token.length === 1 || (token.length <= 3 && !/[aeiou]/.test(token)))) {
    return letters(tokenRaw, lang, ".") + (dotFollows ? "" : ".");
  }
  // A code in a place's name - MG Road, NH 16, APSRTC Bus Stand - read letter by letter.
  if ((kind === "place" || kind === "city") && token.length > 1
    && (!/[aeiouy]/.test(token) || (mixed && /^[A-Z]+$/.test(tokenRaw)))) {
    return letters(tokenRaw, lang, ZWNJ);
  }
  // Two names run together: Ramakrishna, Nagaraju.
  if (kind === "person") {
    for (let cut = 2; cut <= token.length - 2; cut += 1) {
      const a = lookup(NAME_WORDS, token.slice(0, cut), lang), b = lookup(NAME_WORDS, token.slice(cut), lang);
      if (a && b) return fromTelugu(a.te + b.te, lang, { longA: tamilA });
    }
  }
  if (kind === "place" || kind === "city") {
    const suffix = PLACE_SUFFIXES.find(([s]) => token.endsWith(s) && token.length > s.length + 1);
    if (suffix) {
      const stem = spellTelugu(token.slice(0, -suffix[0].length), "place").replace(new RegExp(`${ch(VIRAMA)}$`), "");
      return fromTelugu(stem + suffix[1], lang, { longA: tamilA || (lang === "hi" && endsA) });
    }
  }
  const te = spellTelugu(token, kind === "car" || kind === "color" ? "word" : kind === "person" ? "name" : "place");
  return fromTelugu(te, lang, { longA: tamilA || (lang === "hi" && endsA && kind !== "person"), english: kind === "car" });
}

const cache = new Map();
/**
 * `text` in the script of `lang`, read as `kind`: "person", "car", "place", "city" or
 * "color". English, empty and already-local text come back as they are; so do digits,
 * punctuation and anything not made of English letters.
 */
export function localName(text, lang, kind = "person") {
  const s = String(text ?? "");
  if (!s || !(lang in COL) || !/[A-Za-z]/.test(s)) return s;
  const key = `${lang}|${kind}|${s}`;
  if (cache.has(key)) return cache.get(key);
  let out;
  const city = (kind === "city" || kind === "place") && canonicalCity(s);
  if (city && CITY_NATIVE[city]) out = CITY_NATIVE[city][COL[lang]];
  else if (kind === "color" && COLOR_WORDS[s.trim().toLowerCase()]) out = COLOR_WORDS[s.trim().toLowerCase()][COL[lang]];
  else {
    // Words of letters are written; digits, spaces and punctuation stay as they are, and
    // a code of letters and digits (XUV700, AX5) keeps its digits.
    const mixed = /[a-z]/.test(s);
    out = s.replace(/[A-Za-z]+/g, (w, at) => (kind === "car" && (/[0-9]/.test(s[at - 1] || "") || /[0-9]/.test(s[at + w.length] || ""))
      ? letters(w, lang, ZWNJ) : wordIn(w, lang, kind, { dotFollows: s[at + w.length] === ".", mixed })));
  }
  cache.set(key, out);
  if (cache.size > 3000) cache.delete(cache.keys().next().value);
  return out;
}

/** The city in the reader's script ("Vijayawada" -> "విజయవాడ"); an unlisted one by sound. */
export const localCity = (city, lang) => localName(city, lang, "city");

/** `ln(text, kind)` for the reader's language - what pages call. */
export function useLocalNames() {
  const { lang } = useLanguage();
  return useCallback((text, kind) => localName(text, lang, kind), [lang]);
}
